import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent } from '@parrot-software/design-system';
import { ChevronRight, Download, FileX } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import type { PaymentHistoryViewDto } from '@subscriptions/application/dtos';
import { PaymentStatusBadge, StoreCountBadge } from '@subscriptions/presentation/components/base';
import { displayPaymentValue, getPaymentMethodLabel, handleDownload } from '@subscriptions/presentation/utils';

interface PaymentSummaryCardProps {
  payment: PaymentHistoryViewDto;
  onClick: (payment: PaymentHistoryViewDto) => void;
  showSubscription?: boolean;
}

/**
 * PaymentSummaryCard - Clickable summary card for a single payment.
 *
 * Shared between the mobile payment list (top-level rows) and the detail sheet
 * (grouped children). Shows the payment date, a metadata line with the subscription
 * or store name followed by the payment method, the amount, the status badge and an
 * optional "PDF y XML" button. `showSubscription` leaves only the payment method on that
 * metadata line. Grouped payments (those with
 * `relatedInvoices`) also show a {@link StoreCountBadge} next to the status badge
 * so they can be told apart from single-store payments. The download button is
 * rendered whenever at least one invoice URL is available (parent or child);
 * clicking it does not trigger the card's `onClick`.
 *
 * The owed charge replaces the download with a note saying when the invoice comes, and does not
 * open: there is no charge behind it to break down.
 *
 * @param {PaymentSummaryCardProps} props - Component properties
 * @returns {React.ReactElement} The summary card
 */
export const PaymentSummaryCard = ({ payment, onClick, showSubscription = true }: PaymentSummaryCardProps) => {
  const { t } = useTranslation('paymentHistory');

  const isOverdueCharge = payment.isOverdueCharge === true;
  const relatedInvoicesCount = payment.relatedInvoices?.length ?? 0;
  const hasDownload = Boolean(payment.pdfUrl || payment.xmlUrl);
  const paymentMethodLabel = getPaymentMethodLabel(payment.paymentMethod, payment.paymentMethodDetail, t);

  return (
    <Card
      aria-label={isOverdueCharge ? undefined : t('detail.generalInfo')}
      className={cn({ 'cursor-pointer': !isOverdueCharge })}
      onClick={isOverdueCharge ? undefined : () => onClick(payment)}
      role={isOverdueCharge ? undefined : 'button'}
      tabIndex={isOverdueCharge ? undefined : 0}
    >
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1 min-w-0">
            <p className="text-sm font-medium">{displayPaymentValue(payment.paymentDate)}</p>

            <p className="text-sm font-regular truncate text-muted-foreground">
              {showSubscription
                ? `${displayPaymentValue(payment.subscription)} · ${paymentMethodLabel}`
                : paymentMethodLabel}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm font-semibold">{displayPaymentValue(payment.subscriptionAmount)}</span>
            {!isOverdueCharge && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <PaymentStatusBadge status={payment.status} />
          {relatedInvoicesCount > 0 && <StoreCountBadge count={relatedInvoicesCount} />}
        </div>

        {isOverdueCharge && (
          <div
            className="flex items-start gap-2 border-t pt-3 text-sm text-warning"
            data-testid="payment-history-invoice-on-payment"
          >
            <FileX className="mt-0.5 h-4 w-4 shrink-0" />
            {t('invoiceOnPayment')}
          </div>
        )}

        {hasDownload && (
          <Button
            className="w-full"
            onClick={(event) => {
              event.stopPropagation();
              void handleDownload(payment.pdfUrl, payment.xmlUrl, payment.folio, {
                errorMessage: t('downloadError'),
                successMessage: t('downloadSuccess'),
                successDescription: t('downloadSuccessDescription'),
              });
            }}
            variant="outline"
          >
            <Download className="mr-1 h-4 w-4" />
            {t('downloadPdfXml')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
